import { useEffect, useMemo, useState } from 'react';
import { useProcessInstanceId } from 'domain/gpo/cockpit/instance/processInstanceRoute';
import useProcessEventFlowService, {
  EnrichedEventFlow,
} from 'domain/gpo/cockpit/instance/Show/tracing/processEventFlowService';
import TraceSection from 'domain/gpo/cockpit/instance/Show/tracing/TraceSection';
import MediumHeading from 'domain/gpo/cockpit/instance/Show/MediumHeading';
import { Alert } from 'framework/ui/mui-builder';
import { ListLoadingDecorator } from 'framework/list';
import { Box } from '@mui/material';

type TraceSectionConfig = {
  header: string;
  traces: EnrichedEventFlow['messagesReceived'];
};

const ProcessEventFlowTracing = () => {
  const id = useProcessInstanceId();
  const { getEventFlow } = useProcessEventFlowService();
  const [eventFlow, setEventFlow] = useState<EnrichedEventFlow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setEventFlow(null);

    getEventFlow(id)
      .then(setEventFlow)
      .catch(() => setError('Unable to load process tracing data.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const sections: TraceSectionConfig[] = useMemo(() => {
    if (!eventFlow) {
      return [];
    }

    return [
      { header: 'Messages Received', traces: eventFlow.messagesReceived },
      { header: 'Messages Sent', traces: eventFlow.messagesSent },
      { header: 'Started By', traces: eventFlow.startedBy },
      { header: 'Started Processes', traces: eventFlow.startedProcesses },
    ];
  }, [eventFlow]);

  const hasAnyTraces = sections.some(section => section.traces.length > 0);
  const showEmptyState = !loading && !error && eventFlow && !hasAnyTraces;

  return (
    <div>
      <MediumHeading>Tracing</MediumHeading>
      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}
      {loading && (
        <Box mt={1.5}>
          <ListLoadingDecorator loading items={[]} isSmallLoader>
            <></>
          </ListLoadingDecorator>
        </Box>
      )}
      {!loading &&
        !error &&
        sections
          .filter(section => section.traces.length > 0)
          .map(section => (
            <Box key={section.header} mt={1.5}>
              <TraceSection header={section.header} traces={section.traces} />
            </Box>
          ))}
      {showEmptyState && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          No event relationships found for this process instance.
        </Alert>
      )}
    </div>
  );
};

export default ProcessEventFlowTracing;
